import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { useAuth } from 'contexts/AuthContext';

import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleColumn,
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleSearchFilter,
  ConsoleTable,
  FormToc,
  useQueryInitialFilters,
} from 'components/console';
import { UserSession } from 'stores/AuthenticationStoreAdapter';
import { ClientResult, listClient } from 'stores/ClientStoreAdapter';
import {
  ContractDetailResult,
  ContractListResult,
  ContractStatus,
  getContract,
  listContract,
} from 'stores/ContractStoreAdapter';
import { MasterTypeResult, getContractType } from 'stores/MasterTypeStoreAdapter';
import { MemberListResult, listMember } from 'stores/MemberStoreAdapter';
import { PartnerResult, listPartner } from 'stores/PartnerStoreAdapter';
import { ProjectListResult, listProject } from 'stores/ProjectStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { generateUUID } from 'utils/Common';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

import CommentSection from './components/CommentSection';
import ContractDetail from './components/ContractDetail';
import ContractRequestListTab from './components/ContractRequestListTab';

const yen = (v: number | null | undefined) => (v == null ? '—' : `¥${v.toLocaleString()}`);

const ContractManagement: React.FC = () => {
  const navigate = useNavigate();
  const { userSession, users, managers } = useAuth();
  const [isProgress, setProgress] = useState(true);
  const [contracts, setContracts] = useState<ContractListResult[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<ContractDetailResult | null>(null);
  // 現在適用中の検索パラメータ（更新後・再読み込みで同条件を再検索するため保持）
  const [activeParams, setActiveParams] = useState<
    Record<string, string | boolean | number | undefined>
  >({});

  // 検索条件用マスタデータ
  const [members, setMembers] = useState<MemberListResult[]>([]);
  const [partners, setPartners] = useState<PartnerResult[]>([]);
  const [clients, setClients] = useState<ClientResult[]>([]);
  const [projects, setProjects] = useState<ProjectListResult[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);

  const isManager = userSession.roles.includes('MANAGER');

  // デフォルトフィルター: 課長の場合は managerId = 自分
  const defaultFilters: Filter[] = useMemo(() => {
    if (isManager) {
      return [
        {
          id: generateUUID(),
          key: 'managerId',
          color: '#66BB6A',
          value: { key: userSession.userId, name: `課長: ${userSession.userName}` },
        },
      ];
    }
    return [];
  }, [isManager, userSession.userId, userSession.userName]);

  useEffect(() => {
    listMember({})
      .then(setMembers)
      .catch(() => {});
    listPartner({})
      .then(setPartners)
      .catch(() => {});
    listClient({})
      .then(setClients)
      .catch(() => {});
    listProject({})
      .then(setProjects)
      .catch(() => {});
    getContractType()
      .then(setContractTypes)
      .catch(() => {});
  }, []);

  const fetchContracts = (params: Record<string, string | boolean | number | undefined>) => {
    setProgress(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    listContract(params as any)
      .then((result) => {
        setContracts(result);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('契約の取得に失敗しました');
      });
  };

  // 現在適用中の検索条件で再検索する
  const refetch = () => fetchContracts(activeParams);

  const handleSearch = (filters: Filter[]) => {
    setSelectedDetail(null);
    const params: Record<string, string> = {};
    filters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    setActiveParams(params);
    fetchContracts(params);
  };

  const handleRowClick = (row: ContractListResult) => {
    setProgress(true);
    getContract({ contractId: row.contractId })
      .then((detail) => {
        setSelectedDetail(detail);
        setProgress(false);
      })
      .catch(() => {
        setProgress(false);
        errorDefault('契約詳細の取得に失敗しました');
      });
  };

  // 更新後は共通で「ドロワーを閉じて現在の検索条件で再検索」
  const handleDetailUpdated = () => {
    setSelectedDetail(null);
    refetch();
  };

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'managementId', name: '管理ID', type: 'text' as const, color: '#78C1F3' },
      {
        key: 'personInChargeId',
        name: '担当者',
        type: 'user-select-single' as const,
        color: '#F7C8E0',
        users,
      },
      {
        key: 'managerId',
        name: '課長',
        type: 'manager-select-single' as const,
        color: '#66BB6A',
        managers: managers as unknown as UserResult[],
      },
      {
        key: 'projectId',
        name: 'プロジェクト',
        type: 'project-select-single' as const,
        color: '#FFB74D',
        projects,
      },
      {
        key: 'memberId',
        name: 'メンバー',
        type: 'member-select-single' as const,
        color: '#4FC3F7',
        members,
      },
      {
        key: 'partnerId',
        name: 'パートナー',
        type: 'partner-select-single' as const,
        color: '#CE93D8',
        partners,
      },
      {
        key: 'clientId',
        name: '取引先',
        type: 'client-select-single' as const,
        color: '#A5D6A7',
        clients,
      },
      {
        key: 'contractTypeId',
        name: '契約形態',
        type: 'select' as const,
        color: '#FFAB91',
        values: contractTypes.map((ct) => ({ key: ct.typeCode, name: ct.typeLabel })),
      },
      { key: 'fiscalYear', name: '年度', type: 'text' as const, color: '#90CAF9' },
      { key: 'fiscalMonth', name: '年度月', type: 'text' as const, color: '#80DEEA' },
      {
        key: 'includeTerminated',
        name: '契約終了を含む',
        type: 'check' as const,
        color: '#78909C',
        value: { key: 'true', name: '契約終了を含む' },
      },
    ],
    [users, members, partners, clients, projects, contractTypes, managers],
  );

  // URL クエリパラメータを初期検索条件として反映（担当者/課長の self 解決対応）
  const { queryParams, initialFilters: queryFilters } = useQueryInitialFilters(searchConditions, {
    selfUserId: userSession.userId,
  });
  // 検索バー表示用: 課長デフォルト ＋ クエリ由来（クエリ優先）
  const initialFilters: Filter[] = useMemo(() => {
    const queryKeys = new Set(queryFilters.map((f) => f.key));
    const base = defaultFilters.filter((f) => !queryKeys.has(f.key));
    return [...base, ...queryFilters];
  }, [defaultFilters, queryFilters]);

  // 初期取得: 課長デフォルト ＋ クエリパラメータ（クエリ優先）
  useEffect(() => {
    const params: Record<string, string | boolean | number | undefined> = {};
    defaultFilters.forEach((f) => {
      params[f.key] = f.value.key;
    });
    Object.assign(params, queryParams);
    setActiveParams(params);
    fetchContracts(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const columns: ConsoleColumn<ContractListResult>[] = [
    {
      key: 'managementId',
      header: '管理ID',
      width: 110,
      render: (c) => (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {c.managementId}
          </Box>
          {c.hasPendingRequest && (
            <Chip
              size="small"
              label="申請中"
              color="warning"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
            />
          )}
        </Stack>
      ),
    },
    {
      key: 'status',
      header: '状態',
      width: 84,
      render: (c) => (
        <Chip
          size="small"
          label={ContractStatus[c.status]}
          color={c.status === 'ACTIVE' ? 'success' : 'default'}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      ),
    },
    {
      key: 'projectName',
      header: 'プロジェクト',
      width: 220,
      ellipsis: (c) => c.projectName,
    },
    {
      key: 'memberName',
      header: '要員 / パートナー',
      width: 180,
      ellipsis: (c) => (c.partnerName ? `${c.memberName} / ${c.partnerName}` : c.memberName),
    },
    {
      key: 'managerName',
      header: '課長',
      width: 110,
      ellipsis: (c) => c.managerName,
    },
    {
      key: 'period',
      header: '契約期間',
      width: 170,
      render: (c) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {formatDate(c.contractStartDate)} 〜 {formatDate(c.contractEndDate)}
        </Box>
      ),
    },
    {
      key: 'salesAmount',
      header: '売上',
      width: 100,
      align: 'right' as const,
      render: (c) => (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {yen(c.salesAmount)}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: '100%', overflow: 'hidden' }}>
      <ConsoleHeader
        title="契約"
        count={contracts.length}
        description="契約の検索・確認・変更/終了を行います"
        backLabel="契約管理"
        onBack={() => navigate('/contract')}
        onRefresh={refetch}
      />

      <ConsoleTable
        columns={columns}
        rows={contracts}
        rowKey={(c) => c.contractId}
        selectedKey={selectedDetail?.contractId}
        onRowClick={handleRowClick}
        emptyMessage="契約がありません。検索条件を変更してください。"
        toolbar={
          <ConsoleSearchFilter
            conditions={searchConditions}
            initialFilters={initialFilters}
            onFiltersChange={handleSearch}
            conditionKey="contract-list"
            userProfileKey="contract-list-search"
          />
        }
      />

      <ConsoleDetailDrawer
        open={selectedDetail !== null}
        onClose={() => setSelectedDetail(null)}
        title={selectedDetail?.managementId ?? ''}
        badges={
          selectedDetail && (
            <Chip
              size="small"
              label={ContractStatus[selectedDetail.status]}
              color={selectedDetail.status === 'ACTIVE' ? 'success' : 'default'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )
        }
        width="min(80vw, 1280px)"
      >
        {selectedDetail && (
          <Box sx={{ height: '100%', mx: -2.5, mt: -2.5 }}>
            <ContractDetailView
              detail={selectedDetail}
              userSession={userSession}
              users={users}
              onUpdated={handleDetailUpdated}
            />
          </Box>
        )}
      </ConsoleDetailDrawer>

      <Circular open={isProgress} />
    </Box>
  );
};

// --- ContractDetailView ---
type ContractDetailViewProps = {
  detail: ContractDetailResult;
  userSession: UserSession;
  users: UserResult[];
  onUpdated: () => void;
};

const ContractDetailView: React.FC<ContractDetailViewProps> = ({
  detail,
  userSession,
  users,
  onUpdated,
}) => {
  // 下部タブ。null = 未選択（最小化）。同タブ再選択で最小化、違うタブで最大化。
  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const expanded = selectedTab !== null;
  const handleTabClick = (index: number) =>
    setSelectedTab((prev) => (prev === index ? null : index));
  const contentTabRef = useRef(0);
  if (selectedTab !== null) contentTabRef.current = selectedTab;
  const contentTab = contentTabRef.current;
  const detailScrollRef = useRef<HTMLDivElement>(null);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 詳細上部（本文 + 右側スクロール連動目次） */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* 本文スクロール領域: 内部の sticky ヘッダー（アクションボタン）が
            mx:-2/px:2 で全幅ブリードするため、左右に px:2 を確保して整合させる */}
        <Box ref={detailScrollRef} sx={{ flex: 1, minWidth: 0, overflow: 'auto', px: 2, pt: 1 }}>
          <ContractDetail
            detail={detail}
            userSession={userSession}
            users={users}
            onUpdated={onUpdated}
          />
        </Box>
        <FormToc scrollRef={detailScrollRef} deps={[detail.contractId]} />
      </Box>

      {/* タブ下部 */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" sx={{ px: 2 }}>
          <Tabs value={selectedTab ?? false} sx={{ minHeight: 36, flex: 1 }}>
            <Tab
              label="コメント"
              onClick={() => handleTabClick(0)}
              sx={{ textTransform: 'none', minHeight: 36, py: 0 }}
            />
            <Tab
              label="申請一覧"
              onClick={() => handleTabClick(1)}
              sx={{ textTransform: 'none', minHeight: 36, py: 0 }}
            />
          </Tabs>
          <IconButton
            size="small"
            aria-label={expanded ? '最小化' : '最大化'}
            onClick={() => setSelectedTab(expanded ? null : 0)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Collapse in={expanded} timeout={250} unmountOnExit>
          <Box sx={{ p: 2, height: 360, overflow: 'auto' }}>
            {contentTab === 0 && <CommentSection contractId={detail.contractId} />}
            {contentTab === 1 && <ContractRequestListTab contractId={detail.contractId} />}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default ContractManagement;
